var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { appStrings } from '@backrs_ui/common-ui/src/assets/appStrings';
import BackrsLogoSmallFilled from '@backrs_ui/common-ui/src/assets/images/backrs-logo-filled.svg';
import { AppIcon } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';
import { useImageLoad } from '@backrs_ui/common-ui/src/hooks/useImageLoad';
import { Avatar, Box, Button, Chip, Skeleton, Typography } from '@mui/material';
export var DEEP_DIVE_CARD_IMAGE_HEIGHT_MAX = 200;
export var DEEP_DIVE_CARD_MAX_WIDTH = 300;
var STRINGS = appStrings.modules.deepDive.card;
export var DeepDiveCard = function (_a) {
    var _b, _c;
    var deepDive = _a.deepDive, onCardClick = _a.onCardClick, onCtaClick = _a.onCtaClick;
    var handleCtaClick = function (e) {
        e.stopPropagation();
        onCtaClick === null || onCtaClick === void 0 ? void 0 : onCtaClick(deepDive);
    };
    var image = useImageLoad(deepDive.media);
    var showImage = deepDive.media && !image.imageError;
    var categoryBadges = (_c = (_b = deepDive.tags) === null || _b === void 0 ? void 0 : _b.map(function (tag) { return tag.name; })) !== null && _c !== void 0 ? _c : [];
    return (_jsxs(Box, { sx: cardSx, onClick: function () { return onCardClick === null || onCardClick === void 0 ? void 0 : onCardClick(deepDive); }, children: [_jsxs(Box, { sx: imageWrapSx, children: [showImage ? (_jsxs(_Fragment, { children: [!image.imageLoaded && (_jsx(Skeleton, { variant: "rectangular", animation: "wave", sx: imageSkeletonSx })), _jsx(Box, __assign({ component: "img", src: deepDive.media, alt: deepDive.name, sx: coverImageWithLoadSx(image.imageLoaded) }, image.imageProps))] })) : null, _jsx(Chip, { variant: "badge", size: "sm", label: STRINGS.backrsLabel, sx: backrsChipSx, icon: _jsx(Avatar, { src: BackrsLogoSmallFilled, sx: avatarSx }) })] }), _jsxs(Box, { sx: contentSx, children: [_jsxs(Box, { sx: textAndSupportingTextSx, children: [_jsxs(Box, { sx: titleRowSx, children: [_jsx(Typography, { variant: "text-lg-semibold", sx: titleSx, children: deepDive.name }), (deepDive === null || deepDive === void 0 ? void 0 : deepDive.price) && (_jsxs(Typography, { variant: "text-md-medium", sx: priceSx, children: ["$", deepDive.price] }))] }), _jsxs(Box, { sx: descriptionBadgeSx, children: [_jsx(Typography, { variant: "text-sm-regular", sx: descriptionSx, children: deepDive.nugget_description }), categoryBadges.length > 0 && (_jsx(Box, { sx: badgesContainerSx, children: categoryBadges.map(function (badge) { return (_jsx(Box, { sx: categoryBadgeSx, children: _jsx(Typography, { variant: "text-xs-medium", children: badge }) }, badge)); }) }))] })] }), _jsxs(Button, { id: "deep-dive-schedule-btn", hierarchy: "Secondary gray", size: "lg", fullWidth: true, onClick: handleCtaClick, sx: ctaButtonSx, children: [_jsx(AppIcon, { iconName: "CalendarPlus01", diameter: "20px", color: "currentColor" }), _jsx(Typography, { variant: "text-sm-semibold", children: STRINGS.ctaLabel })] })] })] }));
};
var cardSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        display: 'flex',
        flexDirection: 'column',
        borderRadius: backrs.radius['radius-xl'],
        border: "1px solid ".concat(backrs.colors['Colors/Base/Black']),
        overflow: 'hidden',
        width: '100%',
        height: '100%',
        maxWidth: DEEP_DIVE_CARD_MAX_WIDTH,
        backgroundColor: backrs.colors['Colors/Background/bg-primary'],
        cursor: 'pointer',
        userSelect: 'none',
        transition: 'all 0.3s ease-in-out',
        '&:hover': {
            borderColor: backrs.colors['Colors/Foreground/fg-primary'],
            transform: 'translate(-2px, -2px)',
            boxShadow: backrs.shadows['shadow-lg'],
        },
        '&:active': {
            transform: 'none !important',
            boxShadow: 'none !important',
            transition: 'all 0s !important',
        },
    });
};
var imageWrapSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        height: DEEP_DIVE_CARD_IMAGE_HEIGHT_MAX,
        minHeight: DEEP_DIVE_CARD_IMAGE_HEIGHT_MAX,
        backgroundColor: backrs.colors['Colors/Background/bg-brand-solid-hover'],
        position: 'relative',
        overflow: 'hidden',
        borderBottom: "1px solid ".concat(backrs.colors['Colors/Base/Black']),
    });
};
var coverImageWithLoadSx = function (loaded) { return ({
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    opacity: loaded ? 1 : 0,
    transition: 'opacity 0.3s ease-in-out',
}); };
var imageSkeletonSx = {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
};
var backrsChipSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        borderColor: backrs.colors['Colors/Border/border-primary'],
        paddingLeft: backrs.spacing['spacing-none'],
        gap: backrs.spacing['spacing-lg'],
        position: 'absolute',
        bottom: backrs.spacing['spacing-xl'],
        left: backrs.spacing['spacing-xl'],
        backgroundColor: backrs.colors['Colors/Base/White'],
    });
};
var avatarSx = {
    width: '16px',
    height: '16px',
};
var contentSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: backrs.spacing['spacing-2xl'],
        paddingX: backrs.spacing['spacing-xl'],
        paddingY: backrs.spacing['spacing-2xl'],
        width: '100%',
        flex: 1,
    });
};
var textAndSupportingTextSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: backrs.spacing['spacing-xxs'],
        width: '100%',
    });
};
var titleRowSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        width: '100%',
        gap: backrs.spacing['spacing-md'],
    });
};
var descriptionBadgeSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        display: 'flex',
        flexDirection: 'column',
        gap: backrs.spacing['spacing-md'],
    });
};
var titleSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        wordBreak: 'break-word',
        color: backrs.colors['Colors/Text/text-primary'],
        flex: 1,
    });
};
var priceSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        color: backrs.colors['Colors/Text/text-brand-secondary'],
        whiteSpace: 'nowrap',
        flexShrink: 0,
    });
};
var descriptionSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        color: backrs.colors['Colors/Text/text-tertiary'],
        height: '40px',
        minHeight: '40px',
    });
};
var badgesContainerSx = function () { return ({
    display: 'flex',
    flexWrap: 'wrap',
    gap: '2px',
}); };
var categoryBadgeSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        backgroundColor: backrs.colors['Brand/50'],
        border: "1px solid ".concat(backrs.colors['Brand/200']),
        color: backrs.colors['Brand/700'],
        borderRadius: '16px',
        paddingX: backrs.spacing['spacing-md'],
        paddingY: backrs.spacing['spacing-xxs'],
    });
};
var ctaButtonSx = {
    mt: 'auto',
};
